import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "siteSelect", 
    "userSearch", 
    "userIdField", 
    "searchResults", 
    "siteName", 
    "siteUrl", 
    "featuresContainer", 
    "loadingIndicator",
    "noSiteMessage"
  ]
  static values = { userSiteDataUrl: String }

  connect() {
    this.searchTimeout = null
    this.addFeatureEventListeners()
    this.updateFeatureCount()
    this.loadAvailableSites()
    
    // Hide search results when clicking outside
    document.addEventListener('click', this.handleOutsideClick.bind(this))
  }

  disconnect() {
    document.removeEventListener('click', this.handleOutsideClick.bind(this))
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }

  // Load available sites for the dropdown
  async loadAvailableSites() {
    try {
      const response = await fetch('/admin/client_documentation/available_sites', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (response.ok) {
        const sites = await response.json()
        this.populateSiteSelector(sites)
      } else {
        console.error('Failed to fetch available sites:', response.statusText)
      }
    } catch (error) {
      console.error('Error fetching available sites:', error)
    }
  }

  populateSiteSelector(sites) {
    // Clear existing options except the prompt
    this.siteSelectTarget.innerHTML = '<option value="">Select a site...</option>'
    
    sites.forEach(site => {
      const option = document.createElement('option')
      option.value = site.id
      option.textContent = `${site.name} (${site.type})`
      this.siteSelectTarget.appendChild(option)
    })
  }

  // Handle site selection change
  async siteChanged(event) {
    const siteId = event.target.value
    
    if (!siteId) {
      this.clearUserSearch()
      this.showNoSiteMessage(true)
      return
    }

    this.showNoSiteMessage(false)
    this.clearUserSearch()
  }

  // Handle user search input
  searchUsers(event) {
    const query = event.target.value.trim()
    
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    if (query.length < 2) {
      this.hideSearchResults()
      return
    }

    if (!this.siteSelectTarget.value) {
      this.showNoSiteMessage(true)
      return
    }

    this.showLoading(true)
    
    this.searchTimeout = setTimeout(async () => {
      await this.performUserSearch(query)
    }, 300) // Debounce for 300ms
  }

  async performUserSearch(query) {
    try {
      const siteId = this.siteSelectTarget.value
      const url = `/admin/client_documentation/search_users?q=${encodeURIComponent(query)}&site_id=${siteId}`
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (response.ok) {
        const users = await response.json()
        this.displaySearchResults(users)
      } else {
        console.error('Failed to search users:', response.statusText)
        this.hideSearchResults()
      }
    } catch (error) {
      console.error('Error searching users:', error)
      this.hideSearchResults()
    } finally {
      this.showLoading(false)
    }
  }

  displaySearchResults(users) {
    this.searchResultsTarget.innerHTML = ''
    
    if (users.length === 0) {
      const noResults = document.createElement('div')
      noResults.className = 'px-4 py-3 text-sm text-slate-500 dark:text-slate-400'
      noResults.textContent = 'No clients found'
      this.searchResultsTarget.appendChild(noResults)
    } else {
      users.forEach(user => {
        const option = document.createElement('div')
        option.className = 'px-4 py-3 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 text-sm border-b border-slate-200 dark:border-slate-600 last:border-b-0'
        option.innerHTML = `
          <div class="font-medium text-slate-900 dark:text-white">${user.name}</div>
          <div class="text-slate-500 dark:text-slate-400">${user.email}</div>
        `
        option.addEventListener('click', () => this.selectUser(user))
        this.searchResultsTarget.appendChild(option)
      })
    }
    
    this.showSearchResults()
  }

  selectUser(user) {
    this.userIdFieldTarget.value = user.id
    this.userSearchTarget.value = `${user.name} (${user.email})`
    this.hideSearchResults()
    
    // Load user's site data
    this.loadUserSiteData(user.id)
  }

  async loadUserSiteData(userId) {
    const siteId = this.siteSelectTarget.value
    this.showLoading(true)
    
    try {
      const url = `${this.userSiteDataUrlValue}?user_id=${userId}&site_id=${siteId}`
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (response.ok) {
        const data = await response.json()
        
        if (data.multiple_sites) {
          this.handleMultipleSites(data)
        } else {
          this.populateSiteData(data)
        }
      } else {
        console.error('Failed to fetch user site data:', response.statusText)
        this.showError('Failed to load site data')
      }
    } catch (error) {
      console.error('Error fetching user site data:', error)
      this.showError('Network error while loading site data')
    } finally {
      this.showLoading(false)
    }
  }

  handleMultipleSites(data) {
    // For now, use the default data and show a message about multiple sites
    this.populateSiteData(data.default_data)
    this.showInfo(`This client belongs to multiple sites. Using default configuration. Available sites: ${data.sites.map(s => s.name).join(', ')}`)
  }

  populateSiteData(data) {
    // Auto-populate site information
    if (this.hasSiteNameTarget) {
      this.siteNameTarget.value = data.site_name || ''
    }
    
    if (this.hasSiteUrlTarget) {
      this.siteUrlTarget.value = data.site_url || ''
    }

    // Auto-populate features
    if (data.features && Array.isArray(data.features)) {
      this.populateFeatures(data.features)
    }

    // Auto-generate title if site name is available
    this.autoGenerateTitle(data)
    
    // Show success feedback
    this.showSuccessMessage(data)
  }

  populateFeatures(features) {
    // Clear existing selections
    const checkboxes = this.featuresContainerTarget.querySelectorAll('input[type="checkbox"]')
    checkboxes.forEach(checkbox => {
      checkbox.checked = features.includes(checkbox.value)
    })

    // Update visual feedback and feature count
    this.updateFeatureStats(features.length)
    this.updateFeatureCount()
  }

  // ...existing code for feature handling, messages, etc...

  addFeatureEventListeners() {
    const checkboxes = this.featuresContainerTarget.querySelectorAll('input[type="checkbox"]')
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        this.updateFeatureCount()
      })
    })
  }

  updateFeatureStats(featureCount) {
    // Add a small indicator showing how many features were auto-selected
    let indicator = this.featuresContainerTarget.querySelector('.auto-selected-indicator')
    if (!indicator) {
      indicator = document.createElement('div')
      indicator.className = 'auto-selected-indicator text-sm text-green-600 dark:text-green-400 mt-2'
      this.featuresContainerTarget.parentElement.appendChild(indicator)
    }
    
    if (featureCount > 0) {
      indicator.textContent = `✓ ${featureCount} features auto-selected based on site configuration`
      indicator.style.display = 'block'
    } else {
      indicator.style.display = 'none'
    }
  }

  updateFeatureCount() {
    const checkboxes = this.featuresContainerTarget.querySelectorAll('input[type="checkbox"]:checked')
    const countElement = document.getElementById('selected-features-count')
    if (countElement) {
      countElement.textContent = checkboxes.length
    }
  }

  autoGenerateTitle(data) {
    const titleField = document.querySelector('#client_document_title')
    if (titleField && !titleField.value && data.site_name) {
      titleField.value = `Website Setup Documentation for ${data.site_name}`
    }
  }

  clearUserSearch() {
    this.userSearchTarget.value = ''
    this.userIdFieldTarget.value = ''
    this.hideSearchResults()
    this.clearSiteData()
  }

  clearSiteData() {
    if (this.hasSiteNameTarget) {
      this.siteNameTarget.value = ''
    }
    
    if (this.hasSiteUrlTarget) {
      this.siteUrlTarget.value = ''
    }

    // Clear feature selections
    const checkboxes = this.featuresContainerTarget.querySelectorAll('input[type="checkbox"]')
    checkboxes.forEach(checkbox => {
      checkbox.checked = false
    })

    this.updateFeatureCount()
    this.updateFeatureStats(0)
  }

  showSearchResults() {
    this.searchResultsTarget.classList.remove('hidden')
  }

  hideSearchResults() {
    this.searchResultsTarget.classList.add('hidden')
  }

  showNoSiteMessage(show) {
    if (this.hasNoSiteMessageTarget) {
      this.noSiteMessageTarget.classList.toggle('hidden', !show)
    }
  }

  showLoading(show) {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.toggle('hidden', !show)
    }
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.hideSearchResults()
    }
  }

  showSuccessMessage(data) {
    this.showMessage(
      'success',
      `Site data auto-populated for ${data.site_name || 'selected client'}`,
      'green'
    )
  }

  showError(message) {
    this.showMessage('error', message, 'red')
  }

  showInfo(message) {
    this.showMessage('info', message, 'blue')
  }

  showMessage(type, message, color) {
    const messageDiv = document.createElement('div')
    messageDiv.className = `auto-populate-${type} fixed top-4 right-4 bg-${color}-100 border border-${color}-400 text-${color}-700 px-4 py-3 rounded-lg shadow-lg z-50`
    
    const icon = type === 'success' 
      ? '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>'
      : '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>'
    
    messageDiv.innerHTML = `
      <div class="flex items-center">
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          ${icon}
        </svg>
        <span>${message}</span>
      </div>
    `
    
    document.body.appendChild(messageDiv)
    
    // Auto-remove after appropriate time
    const timeout = type === 'error' ? 5000 : 3000
    setTimeout(() => {
      if (messageDiv.parentNode) {
        messageDiv.parentNode.removeChild(messageDiv)
      }
    }, timeout)
  }
}
